<template>
    <div class="tw-min-h-screen tw-w-screen tw-relative tw-bg-gray-50 tw-antialiased">
        <the-survey-answer-header />

        <div
            class="tw-p-4 tw-pt-8 tw-pb-28 md:tw-pt-24 tw-flex tw-items-center md:tw-items-start tw-justify-center tw-expand"
        >
            <!-- survey submitted -->
            <div
                v-if="hasAnswersSubmitted"
                data-test-id="survey-submit-party"
                class="tw-relative tw-max-w-xs tw-text-center tw-select-none"
            >
                <img
                    src="@/img/icons/highfive.png"
                    class="tw-max-w-xs tw-transition-all"
                    :class="{
                        'tw-animate-bounce tw-transform tw-rotate-45': hasEnabledBounceAnimationOnSuccess,
                    }"
                    alt=""
                />
                <div class="tw-font-bold tw-text-gray-300 tw-text-lg tw-my-8">
                    <i18n path="surveys.participate.successMessage" for="value">
                        <span class="tw-underline">{{ $t("surveys.participate.thankYou") }}</span>
                    </i18n>
                </div>
                <l-base-button
                    :button-types="['secondary', 'raised', 'inline']"
                    @click.native="onClose"
                >
                    {{ $t("surveys.participate.btnCloseSurvey") }}
                </l-base-button>
            </div>

            <!-- survey being filled out -->
            <div v-else class="tw-flex-1 lg:tw-max-w-lg">
                <div v-if="loading" class="tw-w-72">
                    <l-skeleton-loader />
                </div>

                <div v-else-if="error" class="tw-text-center">
                    <l-text-alert
                        class="tw-w-full tw-max-w-lg md:tw-w-auto md:tw-min-w-sm tw-mb-4"
                        type="info"
                        :text="error"
                    />
                    <l-base-button
                        :button-types="['secondary', 'raised', 'inline']"
                        href="#/profile"
                    >
                        {{ $t("surveys.participate.btnCloseSurvey") }}
                    </l-base-button>
                </div>

                <l-survey-intro
                    v-else-if="state === constants.SESSION_STATES.INACTIVE"
                    class="tw-w-full tw-max-w-md md:tw-w-auto md:tw-min-w-sm tw-m-auto"
                    :survey="survey"
                    :highlight-cta="highlightCta"
                    :company-name="companyName"
                />

                <template v-else-if="state === constants.SESSION_STATES.ACTIVE">
                    <l-survey-answer-base
                        class="tw-w-full tw-max-w-lg md:tw-w-auto md:tw-min-w-sm tw-m-auto"
                        :question="activeStep"
                        :company-name="companyName"
                    />
                </template>
            </div>
        </div>

        <the-survey-answer-controls
            v-show="!hasAnswersSubmitted && state === constants.SESSION_STATES.ACTIVE"
            @submitted="onSubmit"
        />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import { SESSION_STATES } from "@/config/survey-answer-session";
import { updateZendeskWidgetSettings } from "@/utils/zendesk-web-widget";

import LSkeletonLoader from "@/components/LSkeletonLoader";
import LSurveyIntro from "@/components/LSurveyAnswer/LSurveyIntro";
import LSurveyAnswerBase from "@/components/LSurveyAnswer/LSurveyAnswerBase";
import LTextAlert from "@/components/LTextAlert";
import LBaseButton from "@/components/LBaseButton";
import TheSurveyAnswerHeader from "@/components/LSurveyAnswer/TheSurveyAnswerHeader";
import TheSurveyAnswerControls from "@/components/LSurveyAnswer/TheSurveyAnswerControls";

export default {
    components: {
        LSkeletonLoader,
        LSurveyIntro,
        LSurveyAnswerBase,
        LTextAlert,
        LBaseButton,
        TheSurveyAnswerHeader,
        TheSurveyAnswerControls,
    },
    data() {
        return {
            hasEnabledBounceAnimationOnSuccess: false,
            hasAnswersSubmitted: false,
            highlightCta: false,
            constants: Object.freeze({
                SESSION_STATES,
            }),
            form: {
                answers: {},
            },
        };
    },
    computed: {
        ...mapGetters({
            loading: "surveys/answerSession/loading",
            state: "surveys/answerSession/state",
            survey: "surveys/answerSession/survey",
            activeStep: "surveys/answerSession/activeStep",
            error: "surveys/answerSession/error",
            activeInteractiveStepNumber: "surveys/answerSession/activeInteractiveStepNumber",
            lastInteractiveStepNumber: "surveys/answerSession/lastInteractiveStepNumber",
            canSkipQuestion: "surveys/answerSession/canSkipQuestion",
            canConfirmAnswer: "surveys/answerSession/canConfirmAnswer",
            hasGivenAnswer: "surveys/answerSession/hasGivenAnswer",
            isMobileOrTablet: "isMobileOrTablet",
        }),
        companyName() {
            return this.survey?.team?.name ?? "";
        },
    },
    async created() {
        // data is loaded here to reduce flickering when switching from the base layout
        // (resolvers await a request before navigation)
        await this.loadData();

        // load unfinished answer state from localStorage
        await this.$store.dispatch("surveys/answerSession/init");
    },
    mounted() {
        if (!this.isMobileOrTablet) {
            window.addEventListener("keyup", this.goToNext);
            window.addEventListener("keypress", this.goToNextAfterInput);
            window.addEventListener("keyup", this.goToPrev);
        }

        // hide 3rd party widgets
        this.disableExternalWidget();
    },
    beforeDestroy() {
        if (!this.isMobileOrTablet) {
            window.removeEventListener("keyup", this.goToNext);
            window.removeEventListener("keypress", this.goToNextAfterInput);
            window.removeEventListener("keyup", this.goToPrev);
        }

        // stop any ongoing party
        this.$confetti.stop();

        // re-enable any 3rd party widgets
        this.enableExternalWidget();
    },
    methods: {
        enableExternalWidget() {
            updateZendeskWidgetSettings({ suppress: false, zIndex: 999999 });
        },
        disableExternalWidget() {
            updateZendeskWidgetSettings({ suppress: true, zIndex: 40 });
        },
        async loadData() {
            await this.$store.dispatch("surveys/answerSession/getSurveyDetails", {
                payload: {
                    surveyId: this.$route.params.surveyId,
                    notificationId: this.$route.params.notificationId,
                    lang: this.$route.query.lang,
                },
            });

            this.highlightCta = true;
            setTimeout(() => {
                this.highlightCta = false;
            }, 2.5 * 1000);
        },
        onNavigateToNextStep(direction = 1) {
            // check required because of easy html manipulation
            if (direction === 1 && !this.canConfirmAnswer && !this.canSkipQuestion) return;

            this.$store.dispatch("surveys/answerSession/navigateToNextStep", {
                payload: { direction },
            });
        },
        onClose() {
            if (this.$route.params?.notificationId === "preview") {
                this.$router.replace(`/surveys/edit/${this.$route.params?.surveyId}/questions`);
                return;
            }

            this.$router.push("/profile");
        },
        onSubmit() {
            this.hasAnswersSubmitted = true;
            this.hasEnabledBounceAnimationOnSuccess = true;

            // partyyyyy!
            this.$confetti.start({
                particlesPerFrame: 0.5,
            });

            setTimeout(() => {
                try {
                    this.hasEnabledBounceAnimationOnSuccess = false;
                    // stop after a few seconds if component still exists
                    this.$confetti.stop();
                } catch {
                    // nothing to do here, component was likely destroyed already
                }
            }, 2.5 * 1000);
        },
        // required for global event listeners
        goToNext(event) {
            if (event.keyCode !== 39) return; // right arrow
            if (["input", "textarea"].includes(event?.target?.nodeName?.toLowerCase())) return; // not in inputs
            if (this.activeInteractiveStepNumber >= this.lastInteractiveStepNumber) return;
            if (!this.canConfirmAnswer && !this.canSkipQuestion) return;
            this.onNavigateToNextStep(1);
        },
        goToNextAfterInput(event) {
            // we reserve a regular enter for navigation
            const isRegularEnter = event.keyCode == 13 && !event.shiftKey;

            if (!isRegularEnter) return; // enter without shift
            if (event.target?.classList?.contains("ignore-enter-submit")) return; // not in keyup selectable nodes
            if (isRegularEnter) event.preventDefault();
            if (this.activeInteractiveStepNumber >= this.lastInteractiveStepNumber) return;
            if (!this.canConfirmAnswer && !this.canSkipQuestion) return;
            this.onNavigateToNextStep(1);
        },
        goToPrev(event) {
            if (event.keyCode !== 37) return; // left arrow
            if (["input", "textarea"].includes(event?.target?.nodeName?.toLowerCase())) return; // not in inputs
            if (this.activeInteractiveStepNumber <= 1) return;
            this.onNavigateToNextStep(-1);
        },
    },
};
</script>

<style scoped>
@media (min-width: 768px) {
    .md\:tw-min-w-sm {
        min-width: 576px;
    }
}

.tw-expand {
    min-height: calc(100vh - 50px - 94px);
}

/** override bootstrap defaults */
.survey-response-textarea.form-control {
    @apply tw-border tw-border-solid tw-bg-gray-50 tw-border-gray-200 tw-rounded;
    resize: vertical !important;
    min-height: 80px;
}
</style>
